import { useState, useEffect, type FormEvent } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/stores/auth.store';
import { verifyExternalMagicLink } from '@/lib/api';
import { ApiClientError } from '@goodchat/api-client';

export function GuestLoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);

  const search = useSearch({ strict: false }) as { token?: string };
  const urlToken = search?.token ?? '';

  const [token, setToken] = useState(urlToken);
  const [fullName, setFullName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(!urlToken);

  // If token is in URL, show the name form directly
  useEffect(() => {
    if (urlToken) {
      setToken(urlToken);
      setShowForm(true);
    }
  }, [urlToken]);

  async function handleTokenSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token.trim()) return;
    setShowForm(true);
  }

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await verifyExternalMagicLink({
        token: token.trim(),
        fullName: fullName.trim(),
        displayName: displayName.trim() || undefined,
      });
      setAuth(
        { ...res.user, isGuest: res.user.isGuest },
        res.accessToken,
        res.refreshToken,
      );
      // Store target org+area so app-shell can navigate directly instead of an extra redirect hop
      if (res.orgId && res.areaId) {
        try {
          sessionStorage.setItem('goodchat_guest_target', JSON.stringify({ orgId: res.orgId, areaId: res.areaId }));
        } catch {}
      }
      navigate({ to: '/' });
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold tracking-tight">Guest Access</h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Enter your invite token and set up your profile
          </p>
        </div>

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
            {error}
          </div>
        ) : null}

        {!showForm ? (
          <form onSubmit={handleTokenSubmit} className="space-y-4">
            <Input
              id="token"
              label="Invite Token"
              type="text"
              required
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste your invite token"
            />
            <Button type="submit" className="w-full">
              Continue
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerify} className="space-y-4">
            <Input
              id="fullName"
              label="Full Name"
              type="text"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your full name"
            />
            <Input
              id="displayName"
              label="Display Name (optional)"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="How others will see you"
            />
            <Button type="submit" loading={loading} className="w-full">
              Join
            </Button>
          </form>
        )}

        <p className="text-center text-sm text-gray-500 dark:text-gray-400">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
